// @tennis-matches/services/trainerstunden/billing-statement-invoice-number

import { resolveBillingStatementDocumentMonth } from "./billing-statement-document-month.js";

const INVOICED_STATUSES = new Set([
  "sent",
  "partially_paid",
  "paid",
  "cancelled",
]);

function __invoiceNumberPeriodYm(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  return (
    resolveBillingStatementDocumentMonth(st) ||
    String(st.period_month ?? "").trim()
  );
}

/**
 * Korrigiert RE-YYYYMM-… wenn persistierte Referenz noch den Leistungsmonat statt Rechnungsmonat trägt.
 *
 * @param {string} reference
 * @param {Record<string, unknown>} statement
 */
function __normalizeBillingStatementInvoiceNumberDisplay(reference, statement) {
  const existing = String(reference ?? "").trim();
  const docYm = __invoiceNumberPeriodYm(statement).replace(/-/g, "");
  if (!existing || !docYm) return existing;
  const m = existing.match(/^RE-(\d{6})-([A-Z0-9]+)$/i);
  if (!m || m[1] === docYm) return existing;
  return `RE-${docYm}-${String(m[2]).toUpperCase()}`;
}

/**
 * @param {Record<string, unknown>|null|undefined} statement
 * @returns {string}
 */
export function buildBillingStatementPaymentReferenceForSend(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  const existing = String(st.payment_reference ?? "").trim();
  if (existing) return existing;
  const id = String(st.id ?? "").trim();
  if (!id) return "";
  const period = __invoiceNumberPeriodYm(st).replace(/-/g, "");
  const suffix = id.slice(0, 8).toUpperCase();
  return period ? `RE-${period}-${suffix}` : `RE-${suffix}`;
}

/**
 * @param {Record<string, unknown>|null|undefined} statement
 * @returns {string}
 */
export function buildBillingStatementStornoPaymentReference(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  const existing = String(st.payment_reference ?? "").trim();
  if (existing) return existing;
  const id = String(st.id ?? "").trim();
  if (!id) return "";
  const period = __invoiceNumberPeriodYm(st).replace(/-/g, "");
  const suffix = id.slice(0, 8).toUpperCase();
  return period ? `ST-${period}-${suffix}` : `ST-${suffix}`;
}

/**
 * @param {Record<string, unknown>|null|undefined} statement
 * @returns {string}
 */
export function buildBillingStatementCreditNotePaymentReference(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  const existing = String(st.payment_reference ?? "").trim();
  if (existing) return existing;
  const id = String(st.id ?? "").trim();
  if (!id) return "";
  const period = __invoiceNumberPeriodYm(st).replace(/-/g, "");
  const suffix = id.slice(0, 8).toUpperCase();
  return period ? `GS-${period}-${suffix}` : `GS-${suffix}`;
}

/**
 * Anzeige-Rechnungsnummer (persistiert in payment_reference oder deterministischer Fallback nach Versand).
 * @param {Record<string, unknown>|null|undefined} statement
 * @returns {string}
 */
export function formatBillingStatementInvoiceNumber(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  const existing = String(st.payment_reference ?? "").trim();
  if (existing)
    return __normalizeBillingStatementInvoiceNumberDisplay(existing, st);

  const status = String(st.status ?? "").trim();
  const sentAt = String(st.sent_at ?? "").trim();
  const isInvoiced =
    INVOICED_STATUSES.has(status) &&
    (status !== "cancelled" || Boolean(sentAt));
  if (!isInvoiced) return "";

  return buildBillingStatementPaymentReferenceForSend(st);
}

/**
 * @param {Record<string, unknown>|null|undefined} statement
 * @returns {boolean}
 */
export function billingStatementHasInvoiceNumber(statement) {
  return Boolean(formatBillingStatementInvoiceNumber(statement));
}

/**
 * Entwurf zurückgezogen — nie versendet, daher ohne RE-/ST-Nummer.
 * @param {Record<string, unknown>|null|undefined} statement
 */
export function isBillingStatementNeverInvoicedCancellation(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  if (String(st.status ?? "").trim() !== "cancelled") return false;
  return !billingStatementHasInvoiceNumber(st);
}
